// libs
import React, { useState, useRef, useEffect, forwardRef } from 'react';
import isEmpty from 'lodash/isEmpty';

// constants
import { ARIA_LABELS } from 'shared/layouts/Coupons/constants';

// context
import { useCartContext } from 'shared/components/CartProvider/context';
import { useDomainConfig } from 'shared/domainConfig/context/context';
import { useLoaderContext } from 'shared/components/LoaderProvider/context';

// components
import { OverlaySheetContext, SHEET_TRANSITION } from 'shared/components/OverlaySheet';

import IOElement from 'shared/components/IOElement';
import ValidityText from 'shared/layouts/Coupons/CouponsExpiry/CouponValdity/CouponValidity';
import CouponOfferError from 'shared/layouts/Coupons/ViewDetailsNudge/CouponOfferError';

// svgs
import CircleXDuotoneIcon from '@nykaa/ui-components/Icons/circle-x-duotone';
import ArrowLeft from '@nykaa/ui-components/Icons/arrow-left';

// helper
import { toXDecimal } from 'shared/layouts/Coupons/helper';

//hook
import useTheme from '@nykaa/ui-components/themes/hooks/useTheme';

//types
import { Theme } from '@nykaa/ui-components/themes/types';

// styles
import {
  ViewDetailsContainer,
  CloseIcon,
  Header,
  OverlaySheet,
  Title,
  Subtitle,
  ActionButtonCartCoupon,
  ActionContainer,
  CopyButton,
  InfoContainer,
  IconContainer,
  Image,
  CouponInfo,
  HeaderTitle,
  HeaderWrapper,
  CouponCode,
  TermsAndContitions,
  TACHeading,
  TACList,
  TACItem,
  ViewMoreButton,
  BottomContainer,
  HighlightedText,
  CouponCodeContainer,
  AppliedText,
  LockIcon,
  StickyContainer,
  CartHeaderTitle,
  InfoContainerWrapper,
  UIComponentLoader,
  LoaderWrapper,
} from './styles';

// color
import { COLORS } from 'shared/styles/base';

// constants
import { TEXTS, ERROR_PAGE } from 'shared/layouts/Coupons/constants';
import { LOGO_TYPE, ROLES, TAB_INDEX } from 'shared/constants';
const VIEW_ALL_TNC_THRESHOLD = 3;
const LOCK_ICON_SIZE = '24px';
const ROOT_MARGIN_IO = '-30px';
const COUPON_PREVIEW = 'COUPON_PREVIEW';

// types
import { CouponDetails } from 'shared/layouts/Coupons/types';
import ChevronRightSmall from '@nykaa/ui-components/Icons/chevron-right-small';
import Logo from 'shared/components/Logo';

// defs
interface ViewDetailsNudgeProps {
  onHide: VoidFunction;
  isEligibleForDeltaCommunication: boolean;
  onApplyClicked?: (coupon: string, appliedFromPopup: boolean) => void;
  couponDetails: CouponDetails;
  onCopyClicked?: (couponCode: string) => void;
  isApplied: boolean;
  actionContainer?: (doHide: VoidFunction) => React.ReactNode | null;
  showHeaderTitle?: boolean;
  className?: string;
  showLockIcon?: boolean;
  sheetTransition?: SHEET_TRANSITION;
  showBackButton?: boolean;
  setParentSheetHeight?: (height: number) => void | null;
  showOverlay?: boolean;
  hasViewProducts?: boolean;
  viewProductsClicked?: () => void;
}

function ViewDetailsNudge({
  onHide,
  onApplyClicked = () => { },
  couponDetails,
  onCopyClicked = () => { },
  isApplied,
  actionContainer,
  className = '',
  showLockIcon = false,
  showBackButton = false,
  sheetTransition = SHEET_TRANSITION.BOTTOM,
  setParentSheetHeight,
  showOverlay = true,
  hasViewProducts = false,
  viewProductsClicked = () => { },
  isEligibleForDeltaCommunication = false,
}: ViewDetailsNudgeProps) {
  const {
    couponCode,
    imageUrl,
    title,
    subtitle,
    tnc,
    discount,
    disabled,
    expiryEpoch = 0,
    deltaTextModified = '',
  } = couponDetails;
  const { currencySymbol } = useCartContext();
  const { showLoader, hideLoader } = useLoaderContext();

  const {
    COLOR: { COUPON_CODE_BACKGROUND },
  } = useDomainConfig();

  const [viewAll, setViewAll] = useState<boolean>(false);
  const [isHidden, setIsHidden] = useState(false);
  const [backgroundOpacity, setBackgroundOpacity] = useState(1);
  const theme: Theme = useTheme();
  const ref = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const { COUPONS_IMAGES } = useDomainConfig();

  useEffect(() => {
    const overlayeElement = overlayRef?.current;
    const onScroll = () => {
      const scrollTop = overlayRef?.current?.scrollTop;
      if (scrollTop / 136 > 0) setBackgroundOpacity(1 - scrollTop / 136);
      if (scrollTop >= 136) {
        ref.current = 1;
      }
    };
    overlayeElement?.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      overlayeElement?.removeEventListener('scroll', onScroll);
    };
  }, [backgroundOpacity, overlayRef, viewAll, setParentSheetHeight]);

  useEffect(() => {
    if (isEmpty(couponDetails) && !showBackButton) {
      return showLoader({});
    } else hideLoader();
  }, [couponDetails, showBackButton, hideLoader, showLoader]);

  const toggleViewAll = () => {
    setViewAll(!viewAll);
    if (viewAll) {
      ref.current = null;
      setBackgroundOpacity(1);
      setIsHidden(false);
    }
  };

  const getList = (list: string[]) => {
    const array = [...list];

    if (viewAll) return array;

    return array.splice(0, VIEW_ALL_TNC_THRESHOLD);
  };

  const onBottomSheetHide = (doHide: VoidFunction) => {
    if (setParentSheetHeight) setParentSheetHeight(overlayRef?.current?.clientHeight);
    doHide();
  };

  const handleActionButtonClicked = (code: string, doHide: VoidFunction) => {
    if (isApplied) {
      onBottomSheetHide(doHide);
      return;
    }

    onApplyClicked(code, true);
  };

  const setHiddenHelper = (doingThis: boolean) => {
    if (!ref?.current) {
      ref.current = 1;
      return;
    }
    setIsHidden(doingThis);
  };

  return (
    <OverlaySheet
      onHide={onHide}
      navKey={COUPON_PREVIEW}
      disableNav={true}
      transitionFrom={sheetTransition}
      hideOnOverlayClick={true}
      className={className}
      showOverlay={showOverlay}
      allowScroll={false}
    >
      <OverlaySheetContext.Consumer>
        {({ doHide }) => (
          <>
            {isEmpty(couponDetails) && showBackButton ? (
              <LoaderWrapper>
                <HeaderWrapper showBottomBorder={false} isHidden={isHidden}>
                  <CloseIcon
                    onClick={() => onBottomSheetHide(doHide)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onBottomSheetHide(doHide);
                      }
                    }}
                    data-at="coupon-btm-close"
                    aria-label={
                      showBackButton ? ARIA_LABELS.GO_BACK : ARIA_LABELS.CLOSE_COUPON_DETAILS
                    }
                    role={ROLES.BUTTON}
                    tabIndex={TAB_INDEX.ACTION_INVISIBILITY}
                  >
                    {showBackButton ? (
                      <ArrowLeft aria-hidden="true" />
                    ) : (
                      <CircleXDuotoneIcon aria-hidden="true" />
                    )}
                  </CloseIcon>
                  <Header backgroundOpacity={backgroundOpacity} />
                </HeaderWrapper>
                <UIComponentLoader indeterminateTime={2} size="medium" value={0} />
              </LoaderWrapper>
            ) : couponDetails?.couponCode === '' ? (
              <CouponOfferError
                showBackButton={showBackButton}
                sheetTransition={sheetTransition}
                {...ERROR_PAGE}
                imageUrl={COUPONS_IMAGES.SOMETHING_WENT_WRONG}
                onHide={onHide}
                setParentSheetHeight={setParentSheetHeight}
                className={className}
                onActionButtonClick={() => onBottomSheetHide(doHide)}
              />
            ) : (
              <ViewDetailsContainer
                ref={overlayRef}
                className="view-details-container"
                data-modal-active="true"
              >
                <>
                  <HeaderWrapper showBottomBorder={false} isHidden={isHidden}>
                    <CloseIcon
                      tabIndex={TAB_INDEX.ACTION_INVISIBILITY}
                      onKeyDown={(event: React.KeyboardEvent) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          onBottomSheetHide(doHide);
                        }
                      }}
                      className="close-icon"
                      onClick={() => onBottomSheetHide(doHide)}
                      data-at="coupon-btm-close"
                      aria-label={
                        showBackButton ? ARIA_LABELS.GO_BACK : ARIA_LABELS.CLOSE_COUPON_DETAILS
                      }
                      role={ROLES.BUTTON}
                    >
                      {showBackButton ? (
                        <ArrowLeft aria-hidden="true" />
                      ) : (
                        <CircleXDuotoneIcon aria-hidden="true" />
                      )}
                    </CloseIcon>
                    <Header className="hasBackground" backgroundOpacity={backgroundOpacity} />
                  </HeaderWrapper>
                  {!disabled && (
                    <>
                      <CartHeaderTitle smallFont={backgroundOpacity < 0.5} isHidden={isHidden}>
                        {!isEligibleForDeltaCommunication ? (
                          <>
                            {!showBackButton && TEXTS.APPLY_AND}
                            <HighlightedText>
                              {!showBackButton ? TEXTS.SAVE : TEXTS.SAVE_OFFER}
                              <span data-test="currency">{currencySymbol}</span>
                              {discount > 0 && showBackButton ? discount : toXDecimal(discount)}
                            </HighlightedText>
                          </>
                        ) : (
                          <>
                            {!showBackButton && isEmpty(deltaTextModified)
                              ? TEXTS.APPLY_AND
                              : isEligibleForDeltaCommunication
                                ? deltaTextModified
                                : ''}
                            {isEmpty(deltaTextModified) && (
                              <HighlightedText>
                                {!showBackButton ? TEXTS.SAVE : TEXTS.SAVE_OFFER}
                                <span data-test="currency">{currencySymbol}</span>
                                {discount > 0 && showBackButton ? discount : toXDecimal(discount)}
                              </HighlightedText>
                            )}
                          </>
                        )}
                      </CartHeaderTitle>
                    </>
                  )}
                  <StickyContainer
                    isHidden={!isHidden}
                    className={'sticky'}
                    showBottomBorder={true}
                  >
                    <CloseIcon
                      onClick={() => onBottomSheetHide(doHide)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onBottomSheetHide(doHide);
                        }
                      }}
                      data-at="coupon-btm-close"
                      aria-label={
                        showBackButton ? ARIA_LABELS.GO_BACK : ARIA_LABELS.CLOSE_COUPON_DETAILS
                      }
                      role={ROLES.BUTTON}
                      tabIndex={TAB_INDEX.ACTION_INVISIBILITY}
                    >
                      {showBackButton ? (
                        <ArrowLeft aria-hidden="true" />
                      ) : (
                        <CircleXDuotoneIcon aria-hidden="true" />
                      )}
                    </CloseIcon>
                    <HeaderTitle className="sticky-title">
                      {!showBackButton && TEXTS.APPLY_AND}
                      <HighlightedText>
                        {!showBackButton ? TEXTS.SAVE : TEXTS.SAVE_OFFER}
                        <span data-test="currency">{currencySymbol}</span>
                        {discount > 0 && toXDecimal(discount)}
                      </HighlightedText>
                    </HeaderTitle>
                  </StickyContainer>
                  <IOElement
                    onHidden={() => setHiddenHelper(true)}
                    onVisible={() => !isHidden && setHiddenHelper(false)}
                    options={{ rootMargin: ROOT_MARGIN_IO }}
                  />
                </>

                <InfoContainerWrapper>
                  <InfoContainer showBottomBorder={true} className="infoContainer">
                    <IconContainer className="iconContainer">
                      {imageUrl === '' || imageUrl === null ? (
                        <Logo type={LOGO_TYPE.COUPONS} />
                      ) : (
                        <Image src={imageUrl} width={66} height={66} alt="coupon logo" />
                      )}
                    </IconContainer>

                    <CouponInfo>
                      <Title>{title}</Title>
                      <Subtitle>{subtitle}</Subtitle>
                      {expiryEpoch > 0 && <ValidityText expiryEpoch={expiryEpoch} />}
                    </CouponInfo>
                  </InfoContainer>
                  {tnc && tnc.length > 0 && (
                    <TermsAndContitions>
                      <TACHeading>{TEXTS.TERMS_AND_CONDITIONS}</TACHeading>
                      <TACList
                        className="termsConditionsList"
                        showLastBottomMargin={tnc.length > VIEW_ALL_TNC_THRESHOLD}
                      >
                        {getList(tnc).map((item, i) => (
                          <TACItem key={`tnc-item-${i}`}>{item}</TACItem>
                        ))}
                      </TACList>
                      {tnc.length > VIEW_ALL_TNC_THRESHOLD && (
                        <ViewMoreButton
                          onClick={toggleViewAll}
                          viewAll={viewAll}
                          className="viewMoreButton"
                        >
                          {viewAll ? TEXTS.VIEW_LESS : TEXTS.VIEW_MORE}
                          <ChevronRightSmall
                            className="chevronIcon"
                            color={COLORS.BRAND_PRIMARY_100}
                          />
                        </ViewMoreButton>
                      )}
                    </TermsAndContitions>
                  )}
                </InfoContainerWrapper>

                <BottomContainer showTopBorder={viewAll}>
                  <CouponCodeContainer isApplied={isApplied} bgColor={COUPON_CODE_BACKGROUND}>
                    {showLockIcon && (
                      <LockIcon color={theme.colors.secondary} size={LOCK_ICON_SIZE} />
                    )}
                    <CouponCode className="couponCode">{couponCode}</CouponCode>
                    {isApplied && <AppliedText>{TEXTS.APPLIED}</AppliedText>}
                  </CouponCodeContainer>
                  {actionContainer ? (
                    <>{actionContainer(() => onBottomSheetHide(doHide))}</>
                  ) : (
                    <ActionContainer>
                      <CopyButton onClick={() => onCopyClicked(couponCode)}>
                        {TEXTS.COPY}
                      </CopyButton>
                      {hasViewProducts ? (
                        <ActionButtonCartCoupon
                          tabIndex={TAB_INDEX.ACTION_INVISIBILITY}
                          role={ROLES.BUTTON}
                          onClick={() => viewProductsClicked()}
                          onKeyDown={(event: React.KeyboardEvent) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              viewProductsClicked();
                            }
                          }}
                          kind={'secondary'}
                          size={'large'}
                          shape={'default'}
                          disabled={disabled}
                        >
                          {TEXTS.VIEW_PRODUCTS}
                        </ActionButtonCartCoupon>
                      ) : (
                        <ActionButtonCartCoupon
                          tabIndex={TAB_INDEX.ACTION_INVISIBILITY}
                          role={ROLES.BUTTON}
                          onClick={() => handleActionButtonClicked(couponCode, doHide)}
                          onKeyDown={(event: React.KeyboardEvent) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              handleActionButtonClicked(couponCode, doHide);
                            }
                          }}
                          kind={'secondary'}
                          size={'large'}
                          shape={'default'}
                          disabled={disabled}
                        >
                          {isApplied ? TEXTS.DISMISS : TEXTS.APPLY}
                        </ActionButtonCartCoupon>
                      )}
                    </ActionContainer>
                  )}
                </BottomContainer>
              </ViewDetailsContainer>
            )}
          </>
        )}
      </OverlaySheetContext.Consumer>
    </OverlaySheet>
  );
}

export default forwardRef(ViewDetailsNudge);
